import { useEffect, useRef, useState } from 'react'
import {
    Accordion, AccordionDetails, AccordionSummary, Autocomplete, Box, Card, CardHeader,
    Chip, Dialog, DialogContent, DialogTitle, IconButton, Tab, Table, TableBody, TableCell,
    TableHead, TableRow, Tabs, TextField, ToggleButton, ToggleButtonGroup, Typography
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import GridOnIcon from '@mui/icons-material/GridOn'
import GroupIcon from '@mui/icons-material/Group'
import CloseIcon from '@mui/icons-material/Close'
import toast from 'react-hot-toast'
import { WrapperTable } from '../components'
import { HttpCacheWorkflow } from 'src/http/workflow/httpCacheWorkflow'
import { EntityWithIdAndDescription, EntityWithIdAndDescriptionFields as F } from 'src/types'
import { downloadFileBlobHelper, downloadFileHelper } from 'src/utils/helpers'
import { HttpReportes } from './api'
import {
    EtapaGrupoSeguridadDTO, EtapaGrupoSeguridadFields as EG, SeguridadWorkflowFullDTO,
    SeguridadWorkflowFullFields as WF, SistemaSeguridadDTO, SistemaSeguridadFields as S,
    UsuarioGrupoSeguridadDTO, UsuarioGrupoSeguridadFields as U
} from './types'

const getDescripcionPermiso = (permisoTipo: number | null) => {
    switch (permisoTipo) {
        case 1: return { label: 'Lectura', color: 'info' as const }
        case 2: return { label: 'Miembro', color: 'primary' as const }
        case 3: return { label: 'Responsable', color: 'success' as const }
        default: return { label: 'Sin permiso', color: 'default' as const }
    }
}

function ReporteSeguridadWorkflow() {
    const paisesRequestRef = useRef(0)

    const [tipos, setTipos] = useState<EntityWithIdAndDescription[]>([])
    const [selectedTipo, setSelectedTipo] = useState<EntityWithIdAndDescription | null>(null)

    const [paises, setPaises] = useState<SistemaSeguridadDTO[]>([])
    const [selectedPais, setSelectedPais] = useState<SistemaSeguridadDTO | null>(null)

    const [etapaGrupos, setEtapaGrupos] = useState<EtapaGrupoSeguridadDTO[]>([])
    const [loadingReporte, setLoadingReporte] = useState(false)
    const [loadingPdf, setLoadingPdf] = useState(false)

    const [usuariosGrupo, setUsuariosGrupo] = useState<{ nombreGrupo: string; usuarios: UsuarioGrupoSeguridadDTO[] } | null>(null)
    const [loadingUsuarios, setLoadingUsuarios] = useState(false)

    const [vista, setVista] = useState<'resumida' | 'detallada'>('resumida')
    const [usuariosPorGrupo, setUsuariosPorGrupo] = useState<Record<number, UsuarioGrupoSeguridadDTO[]>>({})

    const [tab, setTab] = useState<'porSistema' | 'completo'>('porSistema')
    const [reporteCompleto, setReporteCompleto] = useState<SeguridadWorkflowFullDTO[]>([])
    const [loadingCompleto, setLoadingCompleto] = useState(false)
    const [loadingPdfCompleto, setLoadingPdfCompleto] = useState(false)
    const [reporteCompletoCargado, setReporteCompletoCargado] = useState(false)

    useEffect(() => {
        HttpCacheWorkflow.getSistemasTipos().then((data: any[]) => {
            setTipos(data)
            if (data.length === 1) setSelectedTipo(data[0])
        })
    }, [])

    useEffect(() => {
        const requestId = paisesRequestRef.current + 1
        paisesRequestRef.current = requestId

        setPaises([])
        setSelectedPais(null)
        setEtapaGrupos([])

        if (!selectedTipo) return

        HttpReportes.getSeguridadWorkflowSistemasPorTipo(selectedTipo.id).then(data => {
            if (paisesRequestRef.current !== requestId) return
            setPaises(data)
            if (data.length === 1) setSelectedPais(data[0])
        })
    }, [selectedTipo])

    useEffect(() => {
        setEtapaGrupos([])
        setUsuariosPorGrupo({})
        if (!selectedPais) return

        setLoadingReporte(true)
        HttpReportes.getSeguridadWorkflowEtapaGrupo(selectedPais[S.CodSistema])
            .then(setEtapaGrupos)
            .finally(() => setLoadingReporte(false))
    }, [selectedPais])

    useEffect(() => {
        if (vista !== 'detallada') return

        const idsGrupo = Array.from(new Set(
            etapaGrupos.filter(fila => fila[EG.IdGrupo] !== null).map(fila => fila[EG.IdGrupo]!)
        ))
        const idsFaltantes = idsGrupo.filter(id => !(id in usuariosPorGrupo))
        if (idsFaltantes.length === 0) return

        Promise.all(
            idsFaltantes.map(id => HttpReportes.getSeguridadWorkflowUsuariosPorGrupo(id).then(usuarios => [id, usuarios] as const))
        ).then(resultados => {
            setUsuariosPorGrupo(prev => {
                const next = { ...prev }
                resultados.forEach(([id, usuarios]) => { next[id] = usuarios })
                
return next
            })
        })
    }, [vista, etapaGrupos])

    const etapas = Object.values(
        etapaGrupos.reduce((acc, fila) => {
            const key = fila[EG.IdEtapa]
            if (!acc[key]) acc[key] = { idEtapa: key, nombre: fila[EG.NombreEtapa], orden: fila[EG.Orden], grupos: [] as EtapaGrupoSeguridadDTO[] }
            acc[key].grupos.push(fila)

return acc
        }, {} as Record<number, { idEtapa: number; nombre: string; orden: number; grupos: EtapaGrupoSeguridadDTO[] }>)
    ).sort((a, b) => a.orden - b.orden)

    const gruposConPermisoCrearMensaje = Object.values(
        etapaGrupos
            .filter(fila => fila[EG.IdGrupo] !== null && fila[EG.PermiteCargarOperaciones])
            .reduce((acc, fila) => {
                acc[fila[EG.IdGrupo]!] = fila[EG.DescGrupo]!
                
return acc
            }, {} as Record<number, string>)
    )

    useEffect(() => {
        if (tab !== 'completo' || reporteCompletoCargado) return

        setLoadingCompleto(true)
        HttpReportes.getSeguridadWorkflowCompleto()
            .then(data => {
                setReporteCompleto(data)
                setReporteCompletoCargado(true)
            })
            .finally(() => setLoadingCompleto(false))
    }, [tab, reporteCompletoCargado])

    const handleExportarPdfCompleto = () => {
        setLoadingPdfCompleto(true)
        const promise = HttpReportes.getSeguridadWorkflowCompletoPdf()

        toast.promise(promise, { loading: 'Generando PDF', success: 'PDF generado', error: 'Error al generar el PDF' })
            .then(downloadFileBlobHelper)
            .finally(() => setLoadingPdfCompleto(false))
    }

    const escaparCsv = (valor: string | null | undefined) => {
        const texto = valor ?? ''

return /[",\n;]/.test(texto) ? `"${texto.replace(/"/g, '""')}"` : texto
    }

    const handleExportarCsvCompleto = () => {
        const encabezados = ['Sistema', 'Sistema Principal', 'Etapa', 'Grupo', 'Sector Comercial', 'Unidad de Negocio', 'Usuario', 'Permiso', 'Crea Circuito']
        const filas = reporteCompleto.map(fila => [
            fila[WF.NombreSistema], fila[WF.NombreSistemaPrincipal], fila[WF.NombreEtapa], fila[WF.DescGrupo],
            fila[WF.NombreSectorComercial], fila[WF.NombreUnidadNegocio], fila[WF.ApellidoYNombre], fila[WF.Permiso],
            fila[WF.PermiteCargarOperaciones] ? 'Sí' : 'No'
        ].map(escaparCsv).join(';'))

        const csv = [encabezados.join(';'), ...filas].join('\r\n')
        const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
        const url = window.URL.createObjectURL(blob)

        downloadFileHelper(url, `ReporteSeguridadWorkflow_Completo_${new Date().toISOString().slice(0, 10)}.csv`)
        window.URL.revokeObjectURL(url)
    }

    const handleVerUsuarios = (idGrupo: number, nombreGrupo: string) => {
        setLoadingUsuarios(true)
        setUsuariosGrupo({ nombreGrupo, usuarios: [] })
        HttpReportes.getSeguridadWorkflowUsuariosPorGrupo(idGrupo)
            .then(usuarios => setUsuariosGrupo({ nombreGrupo, usuarios }))
            .finally(() => setLoadingUsuarios(false))
    }

    const handleExportarPdf = () => {
        if (!selectedPais || !selectedTipo) return

        setLoadingPdf(true)
        const promise = HttpReportes.getSeguridadWorkflowPdf(
            selectedPais[S.CodSistema], selectedPais[S.NombreSistemaPrincipal], selectedTipo[F.Description], vista
        )

        toast.promise(promise, { loading: 'Generando PDF', success: 'PDF generado', error: 'Error al generar el PDF' })
            .then(downloadFileBlobHelper)
            .finally(() => setLoadingPdf(false))
    }

    return (
        <WrapperTable>
            <Card>
                <CardHeader
                    title='Seguridad de Accesos por Workflow'
                    subheader='Grupos con acceso a cada etapa de un workflow y sus permisos'
                    action={
                        tab === 'porSistema' ? (
                            <IconButton disabled={!selectedPais || loadingPdf} onClick={handleExportarPdf} title='Exportar PDF'>
                                <PictureAsPdfIcon />
                            </IconButton>
                        ) : (
                            <Box sx={{ display: 'flex' }}>
                                <IconButton disabled={loadingCompleto || reporteCompleto.length === 0} onClick={handleExportarCsvCompleto} title='Exportar CSV'>
                                    <GridOnIcon />
                                </IconButton>
                                <IconButton disabled={loadingCompleto || loadingPdfCompleto} onClick={handleExportarPdfCompleto} title='Exportar PDF'>
                                    <PictureAsPdfIcon />
                                </IconButton>
                            </Box>
                        )
                    }
                />

                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mx: 2, mb: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Tab value='porSistema' label='Por sistema' />
                    <Tab value='completo' label='Reporte completo' />
                </Tabs>

                {tab === 'porSistema' && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mx: 2, mb: 2, p: 2, borderRadius: 1, bgcolor: 'action.hover' }}>
                    <Box sx={{ minWidth: 220, flex: 1 }}>
                        <Autocomplete
                            options={tipos}
                            value={selectedTipo}
                            onChange={(_, v) => setSelectedTipo(v)}
                            getOptionLabel={(o: EntityWithIdAndDescription) => o[F.Description]}
                            isOptionEqualToValue={(o, v) => o.id === v?.id}
                            size='small'
                            renderInput={params => <TextField {...params} label='Tipo de Sistema *' />}
                        />
                    </Box>
                    <Box sx={{ minWidth: 220, flex: 1 }}>
                        <Autocomplete
                            disabled={!selectedTipo}
                            options={paises}
                            value={selectedPais}
                            onChange={(_, v) => setSelectedPais(v)}
                            getOptionLabel={(o: SistemaSeguridadDTO) => o[S.NombreSistemaPrincipal] ?? ''}
                            isOptionEqualToValue={(o, v) => o[S.CodSistema] === v?.[S.CodSistema]}
                            size='small'
                            renderInput={params => <TextField {...params} label='País *' />}
                        />
                    </Box>
                </Box>
                )}

                {tab === 'completo' && (
                    <Box sx={{ mx: 2, mb: 2 }}>
                        {loadingCompleto && <Typography sx={{ p: 2 }} color='text.secondary'>Cargando...</Typography>}
                        {!loadingCompleto && reporteCompleto.length === 0 && (
                            <Typography sx={{ p: 2 }} color='text.secondary'>No hay datos para mostrar</Typography>
                        )}
                        {!loadingCompleto && reporteCompleto.length > 0 && (
                            <Box sx={{ overflowX: 'auto' }}>
                                <Table size='small'>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Sistema</TableCell>
                                            <TableCell>Sistema Principal</TableCell>
                                            <TableCell>Etapa</TableCell>
                                            <TableCell>Grupo</TableCell>
                                            <TableCell>Sector Comercial</TableCell>
                                            <TableCell>Unidad de Negocio</TableCell>
                                            <TableCell>Usuario</TableCell>
                                            <TableCell>Permiso</TableCell>
                                            <TableCell>Crea Circuito</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {reporteCompleto.map((fila, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{fila[WF.NombreSistema]}</TableCell>
                                                <TableCell>{fila[WF.NombreSistemaPrincipal]}</TableCell>
                                                <TableCell>{fila[WF.NombreEtapa]}</TableCell>
                                                <TableCell>{fila[WF.DescGrupo]}</TableCell>
                                                <TableCell>{fila[WF.NombreSectorComercial]}</TableCell>
                                                <TableCell>{fila[WF.NombreUnidadNegocio]}</TableCell>
                                                <TableCell>{fila[WF.ApellidoYNombre]}</TableCell>
                                                <TableCell>{fila[WF.Permiso]}</TableCell>
                                                <TableCell>{fila[WF.PermiteCargarOperaciones] ? 'Sí' : 'No'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        )}
                    </Box>
                )}

                {tab === 'porSistema' && selectedPais ? (
                    <Box sx={{ mx: 2, mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                            <ToggleButtonGroup exclusive size='small' value={vista}
                                onChange={(_, v) => v !== null && setVista(v)}>
                                <ToggleButton value='resumida'>Vista resumida</ToggleButton>
                                <ToggleButton value='detallada'>Vista detallada</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        {!loadingReporte && etapaGrupos.length > 0 && (
                            <Card variant='outlined' sx={{ mb: 2 }}>
                                <CardHeader
                                    title='Grupos con permiso para crear mensajes'
                                    titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
                                    sx={{ pb: 0 }}
                                />
                                <Box sx={{ p: 2, pt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {gruposConPermisoCrearMensaje.length === 0 ? (
                                        <Typography color='text.secondary'>Ningún grupo tiene este permiso</Typography>
                                    ) : (
                                        gruposConPermisoCrearMensaje.map(nombreGrupo => (
                                            <Chip key={nombreGrupo} label={nombreGrupo} size='small' color='secondary' variant='outlined' />
                                        ))
                                    )}
                                </Box>
                            </Card>
                        )}
                        {loadingReporte && <Typography sx={{ p: 2 }} color='text.secondary'>Cargando...</Typography>}
                        {!loadingReporte && etapas.length === 0 && (
                            <Typography sx={{ p: 2 }} color='text.secondary'>No hay etapas con grupos con permiso para este workflow</Typography>
                        )}
                        {etapas.map(etapa => {
                            const gruposConPermiso = etapa.grupos.filter(g => g[EG.IdGrupo] !== null)
                            
return (
                                <Accordion key={etapa.idEtapa} disableGutters sx={{ mb: 1, '&:before': { display: 'none' } }}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon color='primary' />}
                                        sx={{
                                            bgcolor: 'background.paper',
                                            border: '2px solid',
                                            borderColor: 'primary.main',
                                            borderRadius: 1
                                        }}>
                                        <Typography variant='h5' sx={{ fontWeight: 700, color: 'primary.main' }}>{etapa.nombre}</Typography>
                                        <Chip size='small' label={`${gruposConPermiso.length} grupo(s)`}
                                            sx={{ ml: 2, bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 600 }} />
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ p: 0 }}>
                                        {gruposConPermiso.length === 0 ? (
                                            <Typography sx={{ p: 2 }} color='text.secondary'>Sin grupos con permiso</Typography>
                                        ) : vista === 'resumida' ? (
                                            <Table size='small'>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Grupo</TableCell>
                                                        <TableCell>Permiso</TableCell>
                                                        <TableCell align='right'>Usuarios</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {gruposConPermiso.map(grupo => {
                                                        const permiso = getDescripcionPermiso(grupo[EG.PermisoTipo])
                                                        
return (
                                                            <TableRow key={grupo[EG.IdGrupo]}>
                                                                <TableCell>{grupo[EG.DescGrupo]}</TableCell>
                                                                <TableCell><Chip size='small' label={permiso.label} color={permiso.color} /></TableCell>
                                                                <TableCell align='right'>
                                                                    <IconButton size='small' title='Ver usuarios activos'
                                                                        onClick={() => handleVerUsuarios(grupo[EG.IdGrupo]!, grupo[EG.DescGrupo]!)}>
                                                                        <GroupIcon fontSize='small' />
                                                                    </IconButton>
                                                                </TableCell>
                                                            </TableRow>
                                                        )
                                                    })}
                                                </TableBody>
                                            </Table>
                                        ) : (
                                            <Table size='small'>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Grupo</TableCell>
                                                        <TableCell>Permiso</TableCell>
                                                        <TableCell>Usuario</TableCell>
                                                        <TableCell>Email</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {gruposConPermiso.flatMap(grupo => {
                                                        const permiso = getDescripcionPermiso(grupo[EG.PermisoTipo])
                                                        const usuarios = usuariosPorGrupo[grupo[EG.IdGrupo]!]

                                                        if (!usuarios) {
                                                            return [
                                                                <TableRow key={`${grupo[EG.IdGrupo]}-loading`}>
                                                                    <TableCell>{grupo[EG.DescGrupo]}</TableCell>
                                                                    <TableCell><Chip size='small' label={permiso.label} color={permiso.color} /></TableCell>
                                                                    <TableCell colSpan={2} sx={{ color: 'text.secondary' }}>Cargando...</TableCell>
                                                                </TableRow>
                                                            ]
                                                        }

                                                        if (usuarios.length === 0) {
                                                            return [
                                                                <TableRow key={`${grupo[EG.IdGrupo]}-sin-usuarios`}>
                                                                    <TableCell>{grupo[EG.DescGrupo]}</TableCell>
                                                                    <TableCell><Chip size='small' label={permiso.label} color={permiso.color} /></TableCell>
                                                                    <TableCell colSpan={2} sx={{ color: 'text.secondary' }}>Sin usuarios activos</TableCell>
                                                                </TableRow>
                                                            ]
                                                        }

                                                        return usuarios.map(usuario => (
                                                            <TableRow key={`${grupo[EG.IdGrupo]}-${usuario[U.IdUsuario]}`}>
                                                                <TableCell>{grupo[EG.DescGrupo]}</TableCell>
                                                                <TableCell><Chip size='small' label={permiso.label} color={permiso.color} /></TableCell>
                                                                <TableCell>{usuario[U.ApellidoYNombre]}</TableCell>
                                                                <TableCell>{usuario[U.Mail]}</TableCell>
                                                            </TableRow>
                                                        ))
                                                    })}
                                                </TableBody>
                                            </Table>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            )
                        })}
                    </Box>
                ) : tab === 'porSistema' ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography color='text.secondary'>Seleccione un tipo de sistema y un país para ver el reporte</Typography>
                    </Box>
                ) : null}
            </Card>

            <Dialog open={!!usuariosGrupo} onClose={() => setUsuariosGrupo(null)} maxWidth='sm' fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Usuarios activos — {usuariosGrupo?.nombreGrupo}
                    <IconButton size='small' onClick={() => setUsuariosGrupo(null)}><CloseIcon fontSize='small' /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {loadingUsuarios && <Typography color='text.secondary'>Cargando...</Typography>}
                    {!loadingUsuarios && usuariosGrupo?.usuarios.length === 0 && (
                        <Typography color='text.secondary'>Este grupo no tiene usuarios activos</Typography>
                    )}
                    {!loadingUsuarios && usuariosGrupo && usuariosGrupo.usuarios.length > 0 && (
                        <Table size='small'>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Usuario</TableCell>
                                    <TableCell>Email</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {usuariosGrupo.usuarios.map(usuario => (
                                    <TableRow key={usuario[U.IdUsuario]}>
                                        <TableCell>{usuario[U.ApellidoYNombre]}</TableCell>
                                        <TableCell>{usuario[U.Mail]}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
            </Dialog>
        </WrapperTable>
    )
}

export default ReporteSeguridadWorkflow
